import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { MCPTool, MCPAction } from '@/types/mcpTool';
import { MCPToolsService } from '@/api/services/mcpToolsService';
import {
  Loader2,
  Plus,
  ExternalLink,
  Edit,
  Trash2,
  Wrench,
  Code,
  Shield,
  ShieldCheck,
  ShieldAlert,
  Box,
} from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface MCPToolDetailViewProps {
  tool: MCPTool | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreateAction?: (tool: MCPTool) => void;
  onEditAction?: (tool: MCPTool, action: MCPAction) => void;
}

export const MCPToolDetailView: React.FC<MCPToolDetailViewProps> = ({
  tool,
  open,
  onOpenChange,
  onCreateAction,
  onEditAction,
}) => {
  const [actions, setActions] = useState<MCPAction[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && tool) {
      fetchToolActions();
    }
  }, [open, tool]);

  const fetchToolActions = async () => {
    if (!tool) return;

    setLoading(true);
    setError(null);

    try {
      const response = await MCPToolsService.fetchMCPToolDetail(tool.id!);
      setActions(response.actions || []);
    } catch (err: any) {
      console.error('Error fetching tool actions:', err);
      setError(err.message || 'Failed to load actions');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAction = () => {
    if (tool) {
      onCreateAction?.(tool);
    }
  };

  const handleEditAction = (action: MCPAction) => {
    if (tool) {
      onEditAction?.(tool, action);
    }
  };

  const getAuthBadge = () => {
    if (!tool) return null;

    if (!tool.auth_configured) {
      return (
        <Badge variant="outline" className="text-gray-600 bg-gray-50">
          <ShieldAlert className="h-3 w-3 mr-1" />
          No Auth
        </Badge>
      );
    }

    if (tool.requires_user_oauth) {
      return (
        <Badge variant="outline" className="text-purple-700 bg-purple-50 border-purple-200">
          <ShieldCheck className="h-3 w-3 mr-1" />
          OAuth 2.0
        </Badge>
      );
    }

    return (
      <Badge variant="outline" className="text-green-700 bg-green-50 border-green-200">
        <Shield className="h-3 w-3 mr-1" />
        {tool.auth_type === 'bearer' ? 'Bearer Token' : tool.auth_type}
      </Badge>
    );
  };

  const getMethodBadge = (method: string) => {
    const colors: Record<string, string> = {
      GET: 'bg-blue-100 text-blue-700',
      POST: 'bg-green-100 text-green-700',
      PUT: 'bg-yellow-100 text-yellow-700',
      DELETE: 'bg-red-100 text-red-700',
      PATCH: 'bg-purple-100 text-purple-700',
    };

    return (
      <Badge className={`${colors[method] || 'bg-gray-100 text-gray-700'} font-mono`}>
        {method}
      </Badge>
    );
  };

  if (!tool) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100">
                <Box className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <DialogTitle className="text-2xl">{tool.name}</DialogTitle>
                <DialogDescription>{tool.description}</DialogDescription>
              </div>
            </div>
          </div>
        </DialogHeader>

        {/* Tool Info Card */}
        <Card className="p-4 bg-gray-50">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Base URL</p>
                <div className="flex items-center gap-2 mt-1">
                  <code className="text-sm bg-white px-2 py-1 rounded border">
                    {tool.base_url}
                  </code>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2"
                    onClick={() => window.open(tool.base_url, '_blank')}
                  >
                    <ExternalLink className="h-3 w-3" />
                  </Button>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {getAuthBadge()}
                <Badge variant="secondary">
                  <Wrench className="h-3 w-3 mr-1" />
                  {actions.length} {actions.length === 1 ? 'Action' : 'Actions'}
                </Badge>
              </div>
            </div>
          </div>
        </Card>

        <Separator />

        {/* Actions Section */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold">Actions</h3>
              <p className="text-sm text-gray-500">
                API endpoints configured for this tool
              </p>
            </div>
            <Button size="sm" onClick={handleCreateAction}>
              <Plus className="h-4 w-4 mr-2" />
              Add Action
            </Button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : error ? (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : actions.length === 0 ? (
            <Card className="p-8 text-center">
              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Wrench className="h-6 w-6 text-gray-400" />
              </div>
              <h4 className="font-medium text-gray-900 mb-1">No actions yet</h4>
              <p className="text-sm text-gray-500 mb-4">
                Add your first action to start using this tool
              </p>
              <Button size="sm" onClick={handleCreateAction}>
                <Plus className="h-4 w-4 mr-2" />
                Create First Action
              </Button>
            </Card>
          ) : (
            <div className="space-y-2">
              {actions.map((action) => (
                <Card
                  key={action.action_name}
                  className="p-4 hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => handleEditAction(action)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <Code className="h-5 w-5 text-gray-400 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-gray-900">
                            {action.action_name}
                          </p>
                          {getMethodBadge(action.http_method)}
                        </div>
                        <code className="text-xs text-gray-500 block mt-1 truncate">
                          {action.endpoint}
                        </code>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 ml-4">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditAction(action);
                        }}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
